import { Injectable, signal } from '@angular/core';

/** What the console currently knows about the on-screen ticket. */
export interface SidekickFocus {
  ticketId: string;
  reference?: string | null;
  orgLabel?: string | null;
}

/**
 * Seam for the assistant's `getPageContext` provider: reports which ticket the
 * signed-in agent currently has open, so BT Sidekick chat can reason about it
 * (`{ ticket_id: ... }`).
 *
 * F1 shipped this as a placeholder that always reported "no focused ticket".
 * F2 (this file) adds the actual focus state: `ticket-detail.component.ts`
 * calls `setFocus(...)` once the ticket loads and `setFocus(null)` on
 * `ngOnDestroy`, so leaving the ticket route reverts the sidekick to general
 * Q&A. `getFocusedTicketId()` stays as the sole accessor `sidekick-panel.
 * component.ts`'s `getPageContext` reads (F2 does not touch that mount
 * logic — see task-F2-brief) and now derives from the richer state below.
 *
 * The emitted page-context key is `ticket_id` (snake_case) — the backend
 * assistant tools read `_internal_context["page_context"]["ticket_id"]`.
 * `reference`/`orgLabel` are held here for a future consumer; today only
 * `ticketId` reaches the panel.
 */
@Injectable({ providedIn: 'root' })
export class SidekickFocusService {
  private readonly focus = signal<SidekickFocus | null>(null);

  setFocus(f: SidekickFocus | null): void {
    this.focus.set(f);
  }

  current(): SidekickFocus | null {
    return this.focus();
  }

  getFocusedTicketId(): string | null {
    return this.focus()?.ticketId ?? null;
  }
}
